import * as React from 'react';
import styled from 'styled-components';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import noop from 'lodash/noop';
import get from 'lodash/get';
import { Colors, RadioButton, RadioGroup, Text, TextField } from '@origin-digital/style-guide';
import { Box } from '@origin-digital/layout';
import { AddressLookup, ConnectionAddressObject } from '@origin-digital/address-lookup';
import FieldValidate from '../../field-validate/field-validate';
import { getPostCodeValidationError } from '../settings-filters';
import { breakpoints } from '../../../common/mediaQuery';
import { FilterSection } from './common';
import ToolTip from '../../tool-tip/tool-tip';
import * as utils from './address-select-utils';
import { interaction } from '@origin-digital/reporting-client';

export const filterAddressId = 'filter-address';

const Container = styled(FilterSection)`
  flex-grow: 1;
  border-right: 1px solid ${Colors.GREY16};
  padding-right: 24px;

  @media screen and (max-width: ${breakpoints.desktop}px) {
    padding: 0 24px;
  }

  @media screen and (max-width: ${breakpoints.tablet}px) {
    border-bottom: 1px solid ${Colors.GREY16};
    padding: 0 24px 24px 24px;
  }

  @media screen and (max-width: ${breakpoints.mobile}px) {
    padding: 0 0 16px 0;
    border-right: none;
  }
`;

const RadioContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin: -8px 0 8px 8px;

  > :first-child {
    margin-right: 24px;
  }
  div {
    margin-bottom: 0;
  }

  .tool-tip {
    margin-top: 8px;
  }

  .tool-tip__content {
    width: 200px;
  }
`;

const PostcodeContainer = styled.div`
  height: 56px;
  margin-bottom: 8px;

  .field-alert__alert {
    margin-top: -32px;
  }
`;

interface AddressOrPostcodeSelectProps {
  address?: ConnectionAddressObject;
  addressId?: string;
  addressApiProps: {
    url: string;
    reverseLookupUrl: string;
    apiKey: string;
    qasAuthToken: string;
  };
  onAddressChange: (address?: ConnectionAddressObject, id?: string) => void;
  postcode: string;
  handlePostcodeChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

interface AddressOrPostcodeSelectState {
  radioValue: 'address' | 'postcode';
  error: string;
  touched: boolean;
}

export class AddressOrPostcodeSelect extends React.Component<
  AddressOrPostcodeSelectProps,
  AddressOrPostcodeSelectState
> {
  constructor(props) {
    super(props);
    const { addressId, postcode, address } = props;
    const initialRadioValue = !!addressId ? 'address' : 'postcode';
    this.state = {
      error: '',
      radioValue: initialRadioValue,
      touched: false,
    };
  }

  componentDidUpdate(prevProps) {
    // Allow radioValue to be controlled
    // Detect when we have been passed a new postcode/address value and update accordingly
    const { postcode, address } = this.props;

    if (address && !isEqual(address, prevProps.address)) {
      this.setState({ radioValue: 'address' });
    } else if (postcode && postcode !== prevProps.postcode && !address) {
      this.setState({ radioValue: 'postcode' });
    }
  }

  handleChange = ({ value }) => {
    interaction('click', {
      appName: 'compare-plans',
      componentType: 'radio',
      id: value,
      label: value,
    });
    this.setState({ radioValue: value });
  };

  _onAddressChange = (address) => {
    if (address && !isEmpty(address.qasAddress) && !isEqual(address, this.props.address)) {
      this.setState({ error: '' });
      const { qasAddress } = address;
      const unitStreetName = !!qasAddress.unit
        ? `${qasAddress.unit} ${qasAddress.streetNumber}`
        : `${qasAddress.streetNumber}`;
      const label = `${unitStreetName} ${qasAddress.streetName} ${qasAddress.city} ${qasAddress.state} ${qasAddress.postcode}`;
      address.qasSearchResult = { label }; // This is needed to populate the address-bar under rates
      try {
        this.props.onAddressChange(address, filterAddressId);
      } catch (e) {
        // prevent any errors from propagating to the address component. Otherwise the spinner will keep spinning inside the address bar.
      }
    }
  };

  private _onBlur = (event: React.FocusEvent<HTMLInputElement>): void => {
    event.persist();
    this.setState({ touched: true }, () => {
      this._validate(event.target.value);
    });
  };

  private _validate = (inputValue?: string): void => {
    if (this.state.touched) {
      const errorMessage = utils.validate(this.props.address, inputValue);
      this.setState({ error: errorMessage });
    }
  };

  /**
   * This is a work around to ensure the Address filter gets recreated if the address prop has been changed.
   * This is because the address prop can be updated by the address field in the rates section.
   *
   * If we don't re-create the component, the address field will not update with the new address (this is a problem with the address component itself).
   *
   * Also, we are using the qas address label, but not the sapAddressId as the sapAddressId is inconsistent even for the same address.
   * If for some reason the label doesn't exist we will fallback to Stringifying the entire address object.
   *
   * @param address
   */
  private generateAddressComponentKey = (address: ConnectionAddressObject) => {
    if (address) {
      return get(address, 'qasSearchResult.label', JSON.stringify(address));
    }
    return '';
  };

  render() {
    const { radioValue, error } = this.state;
    const { address, addressId, addressApiProps, postcode, handlePostcodeChange } = this.props;

    return (
      <Container>
        <Box display="flex" direction="row">
          <RadioContainer>
            <RadioGroup
              name="addressSearch"
              data-id="address-or-postcode"
              value={radioValue}
              onChange={this.handleChange}
            >
              <RadioButton
                name="postcode"
                data-id="postcode-radio"
                onClick={noop}
                label={
                  <Text fontWeight="medium" size="sm">
                    Postcode
                  </Text>
                }
              />
              <RadioButton
                name="address"
                data-id="address-radio"
                onClick={noop}
                label={
                  <Text fontWeight="medium" size="sm">
                    Address
                  </Text>
                }
              />
            </RadioGroup>
            <ToolTip align="right">
              <span>
                Prices depend on your distributor and the type of meter you have. Your postcode is
                good to quickly compare, but sometimes a postcode is covered by more than one
                distributor, so your exact address is better for more accurate rates.
              </span>
            </ToolTip>
          </RadioContainer>
        </Box>
        {radioValue === 'address' ? (
          <Box height="56px" marginBottom={2}>
            <AddressLookup
              key={this.generateAddressComponentKey(address)}
              data-id="top-level-address"
              label="Your connection address"
              value={address}
              error={error}
              addressTranslationApiEndPoint={addressApiProps.url}
              addressReverseLookupApiEndPoint={addressApiProps.reverseLookupUrl}
              addressTranslationApiKey={addressApiProps.apiKey}
              qasAuthToken={addressApiProps.qasAuthToken}
              onChange={this._onAddressChange}
              onBlur={this._onBlur}
              addressId={addressId}
            />
          </Box>
        ) : (
          <PostcodeContainer>
            <FieldValidate
              onBlur={getPostCodeValidationError}
              onChange={getPostCodeValidationError}
            >
              <TextField
                maxLength={4}
                onChange={handlePostcodeChange}
                value={postcode}
                placeholder="Enter your postcode"
                label="Your postcode"
                data-id="postcode-field"
              />
            </FieldValidate>
          </PostcodeContainer>
        )}
        {!!error && <br />}
        <Text size="xs" color="grey80">
          Search by postcode, or enter your address for more accurate rates
        </Text>
      </Container>
    );
  }
}
